// §26.8 (D-586) — Games in progress: the list that makes a running game findable again.
//
// **The bug this exists for.** A play session lives on the server and outlives every window that
// ever showed it — `liveSessionsFor` answers `invited` and `active` and nothing expires an `active`
// one. What did *not* outlive a reload was the shell's memory of which window was on which session
// (`pendingPlay`), so a refreshed tab came back to a board that had forgotten it was a game, while
// the other player sat waiting at a position this screen no longer knew about. Turn-based titles
// now re-attach themselves at mount (D-586), and this is the half of the fix a person can see: the
// games you are in the middle of, in the app that already lists the people you play them with.
//
// Standings' argument for living here, applied again (§30.6, D-369): the roster app already holds
// the roster, so a row saying *Chess with Anna · your move* costs no new surface and lands next to
// the Suggest button that started it.
//
// Every row carries **End game** as well as Resume, and that is not symmetry for its own sake. A
// session nobody is coming back to would otherwise sit `active` for ever and re-attach itself to
// the window every time one opens — so auto-resume needs a way out, and this is it.
import { useCallback, useEffect, useState } from 'react';
import type { LiveGame, MultiplayerGameId, SocialShellAccess } from '@hangar/shared';
import { COPY } from '@/copy';

/**
 * The word for each title, in the app where somebody reads it.
 *
 * The same `COPY.game.suggest*` strings the Suggest menu uses, deliberately: a game called
 * *Conquest* in the menu that started it and something else in the list of what it started would be
 * two names for one thing, which is exactly the class of bug D-347 was.
 */
const GAME_LABELS: Readonly<Record<MultiplayerGameId, string>> = {
  conquest: COPY.game.suggestConquest,
  intercept: COPY.game.suggestIntercept,
  chess: COPY.game.suggestChess,
  backgammon: COPY.game.suggestBackgammon,
  sortie: COPY.game.suggestSortie,
};

const gameLabel = (gameId: MultiplayerGameId): string => GAME_LABELS[gameId] ?? gameId;

/**
 * What the row's status column says.
 *
 * `myTurn` is `null` for co-op and for an invitation nobody has answered, and those two are
 * genuinely different things — so the invitation says so and co-op says nothing rather than
 * inventing a turn neither player has.
 */
export function statusOf(game: LiveGame): string {
  if (game.state === 'invited') return COPY.game.liveGameInvited;
  if (game.myTurn === null) return '';
  return game.myTurn ? COPY.game.liveGameYourTurn : COPY.game.liveGameTheirTurn;
}

export interface LiveGamesProps {
  access?: SocialShellAccess;
}

export function LiveGames({ access }: LiveGamesProps) {
  const [games, setGames] = useState<readonly LiveGame[]>([]);

  const refresh = useCallback(async () => {
    if (access === undefined) return;
    setGames(await access.liveGames().catch(() => [] as LiveGame[]));
  }, [access]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // §26.6 — the doorbell. A move, an accepted card and a cancelled session all arrive as social
  // frames, so the list keeps up through the one refresh path a cold load also takes (D-217).
  useEffect(() => {
    if (access === undefined) return;
    return access.subscribe(() => {
      void refresh();
    });
  }, [access, refresh]);

  const resume = useCallback(
    (game: LiveGame) => {
      access?.resumeGame(game.sessionId, game.gameId);
    },
    [access],
  );

  const end = useCallback(
    async (game: LiveGame) => {
      if (access === undefined) return;
      // §19.4 — through the shell, and the message says what actually happens. "Are you sure?" is
      // not a description of a game ending for somebody who is not looking at this screen.
      const message = COPY.game.liveGameEndConfirm(gameLabel(game.gameId), game.peerName);
      if (!(await access.confirm(message))) return;
      await access.endGame(game.sessionId);
      await refresh();
    },
    [access, refresh],
  );

  // Nothing in progress is the ordinary state, and an empty heading over an empty list would be a
  // permanent reminder of it. The section simply is not there.
  if (games.length === 0) return null;

  return (
    <section className="conn-group conn-live" data-testid="conn-live">
      <h2 className="conn-group-title">{COPY.game.liveGamesTitle}</h2>
      <ul className="conn-list">
        {games.map((game) => (
          <li className="conn-row" key={game.sessionId} data-testid={`conn-live-${game.sessionId}`}>
            <span className="conn-name">
              {COPY.game.liveGameRow(gameLabel(game.gameId), game.peerName)}
            </span>
            <span className="conn-status" data-testid={`conn-live-status-${game.sessionId}`}>
              {statusOf(game)}
            </span>
            <span className="conn-actions">
              <button
                type="button"
                data-testid={`conn-live-resume-${game.sessionId}`}
                onClick={() => resume(game)}
              >
                {COPY.game.liveGameResume}
              </button>
              <button
                type="button"
                data-testid={`conn-live-end-${game.sessionId}`}
                onClick={() => void end(game)}
              >
                {COPY.game.liveGameEnd}
              </button>
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

export default LiveGames;
